// src/app/useSession.js — la session : utilisateur, connexion, déconnexion.
// Déplacé à l'identique depuis src/App.jsx (P-3.5, squelette) : mêmes états,
// même effet d'ouverture, mêmes fonctions ; seuls changent l'emballage et
// les imports.

import { fontsStyle } from './styles-globaux'
import { supabase } from '../supabase'
import { useState, useEffect } from 'react'
import { resolveUserFromSession } from '../lib/session-role'
import { swrClear } from '../lib/swr-cache'
import { oublierSession } from '../lib/data-api'

export function useSession({ setTab }) {
  const [ready,setReady] = useState(false)
  // ── Auth (Supabase Auth) ──
  const [currentUser,setCurrentUser] = useState(null) // {id, email, role:'admin'|'menage'|'orchestrateur'|'proprietaire', logIds}
  // ── LE POINT D'ÉTAT DU BANDEAU EST PARTI AVEC SA PILULE (2026-09-13) ──
  // Cette lecture ne nourrissait QUE lui : plus personne ne l'affiche. La
  // garder aurait posé une requête au montage et à chaque retour d'onglet pour
  // une donnée que l'accueil lit déjà pour son propre bloc
  // (`chargerPropositionsOperateur`).
  const [loginForm,setLoginForm] = useState({email:"",password:"",error:""})
  const [loginLoading,setLoginLoading] = useState(false)
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = fontsStyle
    document.head.appendChild(style)

    let mounted = true

    // SÉCURITÉ : rôle + logIds résolus EXCLUSIVEMENT depuis app_metadata (signé,
    // immuable client). user_metadata (mutable) n'est jamais lu pour l'autorisation
    // → impossible de forger le shell admin via user_metadata. Cf. session-role.js.
    const mapSession = (session) => resolveUserFromSession(session)
    // ⚠️ NE JAMAIS REMPLACER L'UTILISATEUR S'IL N'A PAS CHANGÉ (défaut mesuré
    // le 2026-08-23 : l'écran « Mon planning » de l'agent restait en
    // « Chargement… » pour toujours). Le cycle : chaque requête Supabase émet
    // un événement auth → `setCurrentUser(nouvel objet)` → toute vue qui
    // dépend de `currentUser` (ici `visibleEmails` → `reload`) se relance →
    // nouvelle requête → nouvel événement. Mesure : 3 358 rechargements et
    // 2 300 requêtes `menage_runs` en 6 secondes, écran jamais rendu.
    // Le remède est une IDENTITÉ STABLE : à contenu identique, on garde la
    // référence précédente — React ne voit alors aucun changement.
    const memeUtilisateur = (a, b) => {
      if (a === b) return true
      if (!a || !b) return false
      if (a.id !== b.id || a.email !== b.email || a.role !== b.role) return false
      const l1 = Array.isArray(a.logIds) ? a.logIds : []
      const l2 = Array.isArray(b.logIds) ? b.logIds : []
      if (l1.length !== l2.length || l1.some((x, i) => x !== l2[i])) return false
      const m1 = Array.isArray(a.managed_agents) ? a.managed_agents : []
      const m2 = Array.isArray(b.managed_agents) ? b.managed_agents : []
      return m1.length === m2.length && !m1.some((x, i) => x !== m2[i])
    }
    const poserUtilisateur = (session) => {
      const suivant = mapSession(session)
      setCurrentUser((prec) => (memeUtilisateur(prec, suivant) ? prec : suivant))
    }

    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (mounted) poserUtilisateur(session)
      } catch(e) { console.error('Session restore failed:', e) }
      if (mounted) setReady(true)
    })()

    // Callback SYNC uniquement. Aucune query Supabase ici (`supabase.from(...)`,
    // `.rpc(...)`, etc.) : le callback tient un verrou interne du client auth,
    // et toute requête Supabase essaie elle-même d'acquérir ce verrou → deadlock.
    // Le chargement des données se fait dans un useEffect séparé (voir ci-dessous).
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return
      // m2 (Lot AUTO-REFRESH) — purge du cache SWR sur TOUTE fin de session
      // (session expirée, signOut d'un autre onglet…), pas seulement le clic
      // Déconnexion (handleLogout). swrClear est SYNC et pur (Map mémoire) —
      // aucune query Supabase ici (contrainte verrou auth, cf. commentaire).
      // F13 (tour 4, R2) : toute fin de session oublie aussi le jeton et les
      // signaux de geste (oublierSession : synchrone, aucune requête) — sinon
      // après une session expirée, l'ancien jeton passerait pour une lecture.
      if (_event === 'SIGNED_OUT') { swrClear(); oublierSession() }
      poserUtilisateur(session)
    })

    return () => { mounted = false; sub?.subscription?.unsubscribe() }
  },[])
  // ── Auth functions ──
  const handleLogin = async () => {
    setLoginLoading(true)
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginForm.email.trim(),
        password: loginForm.password,
      })
      if (error) {
        setLoginForm(f=>({...f, error:"Email ou mot de passe incorrect"}))
      } else {
        setLoginForm({email:"",password:"",error:""})
        // setCurrentUser sera déclenché par onAuthStateChange
      }
    } catch(e) {
      console.error('Login error:', e)
      setLoginForm(f=>({...f, error:"Erreur de connexion"}))
    }
    setLoginLoading(false)
  }

  const handleLogout = async () => {
    try { await supabase.auth.signOut() } catch(e) { console.error('Logout error:', e) }
    // F13 (tour 3) : le jeton de la session d'avant ne doit pas passer pour
    // une lecture réussie après une reconnexion dans le même onglet.
    oublierSession()
    // Lot AUTO-REFRESH — purge du cache mémoire SWR (calendrier/config) : un
    // login suivant (autre compte/rôle) ne doit jamais voir les données du
    // précédent, même une fraction de seconde.
    swrClear()
    // setCurrentUser(null) déclenché par onAuthStateChange
    setTab("accueil")
  }
  return { ready, currentUser, loginForm, setLoginForm, loginLoading, handleLogin, handleLogout }
}
